import { sessionFetch } from '@/session/fetch';
import type { TenantScope } from '@/session/identity';
import type { OperationRequirement } from '@/session/operation';
import type { RequestScope } from '@/session/scopedTransport';

// `fetch` options with string-keyed headers, matching how the posting-template
// editor builds its raw channel-API requests.
type BaseChannelsApiRequestOptions = Omit<RequestInit, 'headers' | 'signal'> & {
  headers?: Record<string, string>;
  operationRequirement: OperationRequirement;
};

export type ChannelsApiReadRequestOptions = BaseChannelsApiRequestOptions & { signal?: AbortSignal };
export type ChannelsApiWriteRequestOptions = BaseChannelsApiRequestOptions & { signal?: never };

const tenantScopeFor = (scope: RequestScope): TenantScope => {
  if (!scope || scope.kind !== 'organization') {
    throw new Error('Posting-template channel requests require an explicit organization scope');
  }
  return { kind: 'organization', organization: scope.organization };
};

// Fetch transport retained for the channel endpoints whose callers consume a
// Response body directly. Tenant identity and workflow phase are required at
// every call site; sessionFetch supplies live auth and guards body consumption.
const requestInitFor = (options: BaseChannelsApiRequestOptions): RequestInit => {
  const { operationRequirement, ...requestInit } = options;
  return {
    ...requestInit,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...options.headers,
    },
  };
};

export async function makePostingChannelsReadRequest(scope: RequestScope, url: string, options: ChannelsApiReadRequestOptions): Promise<Response> {
  const method = (options.method ?? 'GET').toUpperCase();
  const isRenderPreview = method === 'POST' && new URL(url, 'https://session.invalid').pathname.endsWith('/atts/posting_template_render');
  if (method !== 'GET' && method !== 'HEAD' && !isRenderPreview) {
    throw new TypeError('Posting-channel read transport only permits GET, HEAD, and the render-preview POST');
  }
  const { operationRequirement } = options;
  const requestOptions = requestInitFor(options);

  return await sessionFetch(url, requestOptions, {
    organizationScope: tenantScopeFor(scope),
    operationRequirement,
    _sessionSemanticRead: isRenderPreview ? true : undefined,
  });
}

export async function makePostingChannelsWriteRequest(scope: RequestScope, url: string, options: ChannelsApiWriteRequestOptions): Promise<Response> {
  const method = (options.method ?? 'GET').toUpperCase();
  if (method === 'GET' || method === 'HEAD') {
    throw new TypeError('Posting-channel write transport requires a mutating method');
  }
  const { operationRequirement } = options;
  return await sessionFetch(url, requestInitFor(options), {
    organizationScope: tenantScopeFor(scope),
    operationRequirement,
  });
}
